import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useVehicleMapDataQuery } from "../../api/network/adminApiServices";
import { GOOGLEMAPS_ENDPOINTS } from "../../api/network/endpoints";
import { TListingQueryParams } from "../../api/store/commonSlice";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import svgIcons from "../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../constants/constants";
import { useLoggedInUserData } from "../../utils/user";
import { crashtrack_tab_slugs } from "./crash-track-admin-details";
import { CrashTrackTabParams } from "./type";

const ScreenCrashTrackTabStreetView = ({
  assetCrashIdAsset,
  assetCrashIdTimestamp,
  onLoadComplete,
  printMode = false,
}: CrashTrackTabParams) => {
  const tab_slug = crashtrack_tab_slugs[5]; // "street_view";
  const { t: tCrashTrackDetails } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage`,
  });
  const { t } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage.tab_${tab_slug}`,
  });
  const hasCalledLoadComplete = useRef(false);

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");

  // fetching crash track data - for intial values of timestamp dropdown
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { crashTrack: crashTrackQueryParams } = listingQueryParams;

  // get selected vehicle data
  const {
    data: dataVehicleMapData,
    isFetching: isFetchingVehicleMapData,
    isError: isErrorVehicleMapData,
  } = useVehicleMapDataQuery(
    {
      account_id: `${thisUserOrganizationId}`,
      device_id: assetCrashIdAsset,
      start_timestamp: crashTrackQueryParams?.start_time || 0,
      end_timestamp: crashTrackQueryParams?.end_time || 0,
      sort_time: "desc",
    },
    {
      skip:
        !assetCrashIdAsset ||
        !crashTrackQueryParams ||
        !crashTrackQueryParams?.start_time ||
        !crashTrackQueryParams?.end_time,
      refetchOnMountOrArgChange: true,
    }
  );
  useEffect(() => {
    if (
      printMode &&
      !isFetchingVehicleMapData &&
      !hasCalledLoadComplete.current
    ) {
      onLoadComplete?.();
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "===== CRASH TRACK TAB LOADED",
          tab_slug,
          new Date().getTime()
        );
      hasCalledLoadComplete.current = true;
    }
  }, [printMode, isFetchingVehicleMapData, onLoadComplete]);

  const [selectedTrip, setSelectedTrip] = useState<number | null>(null);
  const [sanitizedVehicleMapData, setSanitizedVehicleMapData] = useState<
    VehicleMapDataPoint[][]
  >([]);
  const [streetviewTripPoints, setStreetviewTripPoints] = useState<
    VehicleMapDataPoint[]
  >([]);

  // sanitize vehicle map data
  useEffect(() => {
    if (!!dataVehicleMapData && !isFetchingVehicleMapData) {
      const newSanitizedVehicleMapData: VehicleMapDataPoint[][] = Object.keys(
        dataVehicleMapData
      )
        .filter((key) => {
          const tripPoints = dataVehicleMapData[key];
          const assetCrashIdTimestampInt = parseInt(`${assetCrashIdTimestamp}`);
          return !!assetCrashIdTimestamp
            ? !!tripPoints.find(
                (tripPoint) => tripPoint.timestamp === assetCrashIdTimestampInt
              )
            : true;
        })
        .map((key) => {
          const tripPoints = dataVehicleMapData[key];
          return tripPoints.map((tripPoint) => {
            return {
              ...tripPoint,
              // ignitionindex: dataSingleVehicle?.ignitionindex || null,
            };
          });
        });
      setSanitizedVehicleMapData(newSanitizedVehicleMapData);
      const selectedTripIndex = newSanitizedVehicleMapData.findIndex(
        (tripPoints) =>
          tripPoints.some(
            (tripPoint) =>
              tripPoint.timestamp === parseInt(`${assetCrashIdTimestamp}`)
          )
      );
      setSelectedTrip(selectedTripIndex);
      // select five trip points (two right before assetCrashIdTimestamp, the assetCrashIdTimestamp and two right after assetCrashIdTimestamp)
      if (selectedTripIndex !== -1) {
        const tripPoints = newSanitizedVehicleMapData[selectedTripIndex];
        const crashPointIndex = tripPoints.findIndex(
          (tripPoint) =>
            tripPoint.timestamp === parseInt(`${assetCrashIdTimestamp}`)
        );
        const startIndex = Math.max(0, crashPointIndex - 2);
        const endIndex = Math.min(tripPoints.length, crashPointIndex + 3);
        setStreetviewTripPoints(tripPoints.slice(startIndex, endIndex));
      } else {
        setStreetviewTripPoints([]);
      }
    }
  }, [dataVehicleMapData, isFetchingVehicleMapData]);

  // carousel
  const [currentIndex, setCurrentIndex] = useState(0);
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };
  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev < streetviewTripPoints.length - 1 ? prev + 1 : prev
    );
  };

  if (isFetchingVehicleMapData)
    return (
      <div className="flex-grow flex items-center justify-center min-h-64">
        <img src={svgIcons.LoadingAnimation} alt="" className="size-12" />
      </div>
    );

  if (isErrorVehicleMapData || streetviewTripPoints.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 text-center">
        <p className="font-bold text-xl text-gray-400 px-8">
          {tCrashTrackDetails("tab_data_fetch_error")}
        </p>
      </div>
    );
  }

  return (
    <div className="printable-landscape-page flex flex-col gap-4 p-4">
      <h2 className="font-semibold text-lg">{t("heading")}</h2>
      <div className="flex flex-col Xbg-white rounded-lg">
        {/* Carousel of 5 Google Maps Street View points with left/right arrows */}
        <div className="flex flex-col items-center">
          <div className="flex items-center justify-center gap-4 p-5 w-full">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="p-4 rounded-full enabled:bg-accent-blue-paleO66 enabled:hover:bg-accent-blue-pale enabled:focus:bg-accent-blue-pale disabled:opacity-30"
              aria-label="Previous"
            >
              <img src={svgIcons.LeftArrowIcon} alt="" />
            </button>
            <div
              className={`relative flex-grow ${printMode ? "pt-[45%]" : "pt-[60%]"}`}
            >
              {streetviewTripPoints.length > 0 && (
                <iframe
                  title="Street View"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  className="absolute top-0 left-0 right-0 bottom-0 border-0 rounded-lg"
                  src={GOOGLEMAPS_ENDPOINTS.STREETVIEW_IFRAME_EMBED(
                    streetviewTripPoints[currentIndex].latitude,
                    streetviewTripPoints[currentIndex].longitude,
                    streetviewTripPoints[currentIndex].timestamp
                  )}
                  allowFullScreen
                />
              )}
            </div>
            <button
              onClick={handleNext}
              disabled={currentIndex === streetviewTripPoints.length - 1}
              className="p-4 rounded-full enabled:bg-accent-blue-paleO66 enabled:hover:bg-accent-blue-pale enabled:focus:bg-accent-blue-pale disabled:opacity-30"
              aria-label="Next"
            >
              <img src={svgIcons.RightArrowIcon} alt="" />
            </button>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {currentIndex + 1} / {streetviewTripPoints.length}
          </div>
        </div>

        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && !printMode && (
          <div className="text-[8px] break-all flex gap-2 w-11/12">
            {"*"
              .repeat(5)
              .split("")
              .map((_, i) => (
                <pre key={i} className="max-w-48 overflow-auto">
                  {JSON.stringify(streetviewTripPoints?.[i] || null, null, 2)}
                </pre>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
export default ScreenCrashTrackTabStreetView;
